import { useState, useMemo, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { useRealEstate } from '../../contexts/RealEstateContext';
import { C21_COLORS } from '../../constants/realEstate';

const formatPrice = (price) => {
  return `$${price.toLocaleString()}`;
};

const formatPriceShort = (price) => {
  return `$${(price / 1000).toFixed(0)}K`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Stat Card Component
const StatCard = ({ icon, label, value }) => (
  <Card
    elevation={0}
    sx={{
      height: '100%',
      border: '1px solid',
      borderColor: C21_COLORS.lightGold,
      borderRadius: 2,
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transform: 'translateY(-2px)',
        borderColor: C21_COLORS.gold,
      },
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            backgroundColor: `${C21_COLORS.gold}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: C21_COLORS.black }}>
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Info Item Component
const InfoItem = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
    <Box
      sx={{
        p: 1,
        borderRadius: 1.5,
        backgroundColor: `${C21_COLORS.gold}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ color: C21_COLORS.black }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const BuyerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { getBuyerById, getBuyerViewings, getPropertyById } = useRealEstate();
  const buyer = getBuyerById(id);
  const viewings = getBuyerViewings(id);

  const [expandedViewings, setExpandedViewings] = useState({});

  // Calculate statistics
  const stats = useMemo(() => {
    if (!viewings) return { total: 0, withOffer: 0, totalValue: 0, avgPrice: 0 };
    const total = viewings.length;
    const withOffer = viewings.filter((v) => v.offerSubmitted).length;
    const totalValue = viewings.reduce((sum, v) => sum + v.price, 0);
    const avgPrice = total > 0 ? totalValue / total : 0;
    return { total, withOffer, totalValue, avgPrice };
  }, [viewings]);

  if (!buyer) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/real-estate/buyers')}
          sx={{ mb: 2, color: 'text.secondary' }}
        >
          Back to Buyers
        </Button>
        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'grey.100',
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Buyer Not Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The requested buyer profile does not exist.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const toggleViewingExpand = (viewingId) => {
    setExpandedViewings((prev) => ({
      ...prev,
      [viewingId]: !prev[viewingId],
    }));
  };

  return (
    <Box>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/real-estate/buyers')}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back to Buyers
      </Button>

      {/* Header Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: C21_COLORS.lightGold,
          borderRadius: 2,
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* Accent Bar */}
        <Box sx={{ width: '100%', height: 6, backgroundColor: C21_COLORS.gold }} />

        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  backgroundColor: C21_COLORS.gold,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: C21_COLORS.black,
                  fontSize: '1.25rem',
                  fontWeight: 600,
                }}
              >
                {buyer.name.split(' ').map((n) => n[0]).join('')}
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                  {buyer.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">{buyer.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">{buyer.phone}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box>
              {buyer.preApproved ? (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  label="Pre-Approved"
                  color="success"
                  sx={{ fontWeight: 500 }}
                />
              ) : (
                <Chip
                  icon={<WarningIcon sx={{ fontSize: 16 }} />}
                  label="Pending Approval"
                  color="warning"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<VisibilityIcon sx={{ fontSize: 20, color: C21_COLORS.gold }} />}
            label="Properties Viewed"
            value={stats.total}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<SendIcon sx={{ fontSize: 20, color: C21_COLORS.gold }} />}
            label="Offers Made"
            value={stats.withOffer}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<AttachMoneyIcon sx={{ fontSize: 20, color: C21_COLORS.gold }} />}
            label="Total Value Viewed"
            value={formatPriceShort(stats.totalValue)}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            icon={<HomeIcon sx={{ fontSize: 20, color: C21_COLORS.gold }} />}
            label="Avg. Property Price"
            value={formatPriceShort(stats.avgPrice)}
          />
        </Grid>
      </Grid>

      {/* Buyer Details Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: C21_COLORS.lightGold,
          borderRadius: 2,
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Box sx={{ width: 4, height: 20, borderRadius: 1, backgroundColor: C21_COLORS.gold }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: C21_COLORS.black }}>
              Buyer Preferences
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <InfoItem
                icon={<AttachMoneyIcon sx={{ fontSize: 18, color: C21_COLORS.gold }} />}
                label="Budget Range"
                value={`${formatPriceShort(buyer.priceRange.min)} - ${formatPriceShort(buyer.priceRange.max)}`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <InfoItem
                icon={<BedIcon sx={{ fontSize: 18, color: C21_COLORS.gold }} />}
                label="Bedrooms"
                value={`${buyer.bedrooms}+ Beds`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <InfoItem
                icon={<BathtubIcon sx={{ fontSize: 18, color: C21_COLORS.gold }} />}
                label="Bathrooms"
                value={`${buyer.bathrooms}+ Baths`}
              />
            </Grid>
            {buyer.preApprovalAmount && (
              <Grid item xs={6} sm={3}>
                <InfoItem
                  icon={<CheckCircleIcon sx={{ fontSize: 18, color: '#2e7d32' }} />}
                  label="Pre-Approval Amount"
                  value={formatPrice(buyer.preApprovalAmount)}
                />
              </Grid>
            )}
          </Grid>

          {/* Preferred Locations */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <LocationOnIcon sx={{ fontSize: 18, color: C21_COLORS.gold }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Preferred Locations
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {buyer.preferredLocations.map((loc) => (
                <Chip
                  key={loc}
                  label={loc}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 1,
                    borderColor: C21_COLORS.lightGold,
                    '&:hover': { borderColor: C21_COLORS.gold },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Notes */}
          {buyer.notes && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: '#faf8f5',
                borderRadius: 1.5,
                borderLeft: `3px solid ${C21_COLORS.gold}`,
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
                Notes
              </Typography>
              <Typography variant="body2">
                {buyer.notes}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Property Viewings Card */}
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: C21_COLORS.lightGold,
          borderRadius: 2,
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Box sx={{ width: 4, height: 20, borderRadius: 1, backgroundColor: C21_COLORS.gold }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: C21_COLORS.black }}>
              Property Viewings
            </Typography>
            <Chip
              label={viewings.length}
              size="small"
              sx={{
                ml: 1,
                height: 22,
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: C21_COLORS.gold,
                color: C21_COLORS.black,
              }}
            />
          </Box>

          <TableContainer
            sx={{
              border: '1px solid',
              borderColor: C21_COLORS.lightGold,
              borderRadius: 1.5,
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#faf8f5' }}>
                  <TableCell width={40} />
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Property</TableCell>
                  {!isMobile && <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Date</TableCell>}
                  {!isMobile && <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Price</TableCell>}
                  {!isTablet && <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Commission</TableCell>}
                  <TableCell sx={{ fontWeight: 600, color: C21_COLORS.black }}>Offer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <HomeIcon sx={{ fontSize: 40, color: C21_COLORS.lightGold, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No viewings recorded yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  viewings.map((viewing) => {
                    const property = getPropertyById(viewing.propertyId);
                    return (
                      <Fragment key={viewing.id}>
                        <TableRow
                          hover
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#faf8f5' },
                          }}
                          onClick={() => toggleViewingExpand(viewing.id)}
                        >
                          <TableCell sx={{ borderBottom: expandedViewings[viewing.id] ? 'none' : undefined }}>
                            <IconButton size="small">
                              {expandedViewings[viewing.id] ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ borderBottom: expandedViewings[viewing.id] ? 'none' : undefined }}>
                            <Typography variant="body2" fontWeight={500}>
                              {viewing.propertyAddress}
                            </Typography>
                            {property && (
                              <Typography variant="caption" color="text.secondary">
                                {property.bedrooms} bed, {property.bathrooms} bath
                              </Typography>
                            )}
                          </TableCell>
                          {!isMobile && (
                            <TableCell sx={{ borderBottom: expandedViewings[viewing.id] ? 'none' : undefined }}>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(viewing.dateSeen)}
                              </Typography>
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell sx={{ borderBottom: expandedViewings[viewing.id] ? 'none' : undefined }}>
                              <Typography variant="body2" fontWeight={500}>
                                {formatPrice(viewing.price)}
                              </Typography>
                            </TableCell>
                          )}
                          {!isTablet && (
                            <TableCell sx={{ borderBottom: expandedViewings[viewing.id] ? 'none' : undefined }}>
                              <Chip
                                label={`${viewing.compensation}%`}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  backgroundColor: `${C21_COLORS.gold}20`,
                                  color: C21_COLORS.darkGold,
                                  border: `1px solid ${C21_COLORS.gold}`,
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ borderBottom: expandedViewings[viewing.id] ? 'none' : undefined }}>
                            {viewing.offerSubmitted ? (
                              <Chip
                                icon={<SendIcon sx={{ fontSize: 12 }} />}
                                label="Yes"
                                size="small"
                                color="success"
                                sx={{ height: 22, fontSize: '0.75rem' }}
                              />
                            ) : (
                              <Chip
                                label="No"
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.75rem' }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0, border: 'none' }}>
                            <Collapse in={expandedViewings[viewing.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 2, backgroundColor: '#faf8f5' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                  <Box
                                    sx={{
                                      width: 4,
                                      height: 16,
                                      borderRadius: 1,
                                      backgroundColor: C21_COLORS.gold,
                                    }}
                                  />
                                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: C21_COLORS.black }}>
                                    Client Feedback
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    p: 2,
                                    backgroundColor: 'white',
                                    borderRadius: 1.5,
                                    borderLeft: `3px solid ${C21_COLORS.gold}`,
                                    border: '1px solid',
                                    borderColor: C21_COLORS.lightGold,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontStyle: viewing.comments ? 'normal' : 'italic',
                                      color: viewing.comments ? 'text.primary' : 'text.secondary',
                                    }}
                                  >
                                    {viewing.comments || 'No feedback provided.'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BuyerDetail;
